import React from 'react';
import { Package, User, Shield, Settings, ShoppingCart, ArrowLeft, FileText, Plus, Sun, Moon, Home, Languages } from 'lucide-react';
import { useTheme } from '../ui/ThemeContext';
import { useLanguage } from '../i18n/LanguageContext';
import LiquidGlassIconButton from '../ui/LiquidGlassIconButton';
import NotificationBell from './NotificationBell';

const Header = ({ user, cart, purchaseRequests, drafts, currentView, onLogout, onNavigate, onOpenRequests, siteTitle, logo, ociMode }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();

  // Sichere Berechnung der Entwürfe-Anzahl
  const draftsCount = React.useMemo(() => {
    if (!Array.isArray(drafts)) {
      return 0;
    }
    return drafts.length;
  }, [drafts]);

  // Sichere Berechnung der Warenkorb-Anzahl: Anzahl POSITIONEN (Zeilen),
  // nicht die Summe der Mengen.
  // Beschaffungskontext (Entscheidung Taras 28.07.): die drei Beschaffungs-Icons (+BANF,
  // BANF-Liste, Warenkorb) erscheinen NUR in Modulen, die Beschaffungsfunktionen nutzen —
  // Beschaffung selbst UND Lagerverwaltung (Schnell-BANF/BANF-Status). Auf Home/Settings/
  // Admin bleibt die Shell neutral (Fiori-Muster; spaeter uebernimmt eine globale
  // Benachrichtigungs-Glocke die Zaehler modulunabhaengig). Liste bewusst erweiterbar (PM ...).
  const PROCUREMENT_CONTEXT_VIEWS = ['catalogs', 'products', 'product-detail', 'favorites', 'cart', 'create-banf', 'purchase-requests', 'lager'];
  const inProcurementContext = PROCUREMENT_CONTEXT_VIEWS.includes(currentView) || Boolean(ociMode);

  const cartItemCount = React.useMemo(() => {
    if (!Array.isArray(cart)) {
      console.warn('Cart is not an array:', cart);
      return 0;
    }

    return cart.length;
  }, [cart]);

  // Berechnung der ausstehenden BANF-Anfragen
  const pendingRequestsCount = React.useMemo(() => {
    if (!Array.isArray(purchaseRequests)) {
      return 0;
    }

    // Für verschiedene Rollen unterschiedliche Zähllogik
    if (user?.role === 'purchaser') {
      // Purchaser: Nur eigene ausstehende Anfragen
      return purchaseRequests.filter(request => request.status === 'pending').length;
    } else if (user?.role === 'supervisor') {
      // Supervisor: Ausstehende Anfragen aus eigener Kostenstelle
      return purchaseRequests.filter(request => 
        request.status === 'pending' && 
        user.costCenter === (request.requester_cost_center_actual || request.requester_cost_center)
      ).length;
    } else if (user?.role === 'admin') {
      // Admin: Alle ausstehenden Anfragen
      return purchaseRequests.filter(request => request.status === 'pending').length;
    }
    
    return 0;
  }, [purchaseRequests, user?.role, user?.costCenter]);

  return (
    <div className="sticky top-0 z-40 relative">
      {/* Liquid Glass Header Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gray-900/20 border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.3)] backdrop-blur-2xl'
        : 'bg-white/80 border-b border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] backdrop-blur-md'
      }`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-r from-blue-900/5 via-transparent to-purple-900/5'
        : ''
      }`}></div>
      
      
      <div className="relative max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Liquid Glass Logo & Title Section */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="flex items-center">
              {logo && logo !== 'null' && logo !== '' ? (
                <div className="relative group/logo mr-2 sm:mr-3">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm"></div>
                  <div className={`relative ${isDarkMode 
                    ? 'bg-gradient-to-br from-slate-600/40 via-gray-600/30 to-slate-700/40' 
                    : 'bg-gradient-to-br from-gray-300/60 via-slate-300/50 to-gray-400/60'
                  } backdrop-blur-xl w-8 h-8 sm:w-10 sm:h-10 rounded-2xl flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.2)] border border-white/20`}>
                    <>
                      <img 
                        src={logo} 
                        alt={t('header.portalLogoAlt')}
                        className="w-6 h-6 sm:w-8 sm:h-8 object-contain relative z-10"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.nextElementSibling.style.display = 'block';
                        }}
                      />
                      <div className="absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm group-hover/logo:scale-110 transition-transform duration-300"></div>
                    </>
                  </div>
                </div>
              ) : null}
              <div 
                className="relative group/logo mr-2 sm:mr-3" 
                style={{ display: (logo && logo !== 'null' && logo !== '') ? 'none' : 'block' }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm"></div>
                <div className={`relative ${isDarkMode 
                  ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60' 
                  : 'bg-gradient-to-br from-gray-300/60 via-slate-300/50 to-gray-400/60'
                } backdrop-blur-xl w-8 h-8 sm:w-10 sm:h-10 rounded-2xl flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.2)] border border-white/20`}>
                  <Package className={`w-4 h-4 sm:w-6 sm:h-6 ${isDarkMode ? 'text-white/90' : 'text-gray-600'} relative z-10`} />
                  <div className="absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm group-hover/logo:scale-110 transition-transform duration-300"></div>
                </div>
              </div>
              <div className="flex flex-col">
                {/* Liquid Glass Site Title */}
                <h1
                  className={`hidden sm:block text-xl font-bold ${isDarkMode
                    ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent drop-shadow-lg'
                    : 'text-slate-900'
                  } cursor-pointer hover:scale-105 transition-all duration-200`}
                  onClick={() => window.location.reload()}
                  title={t('header.reloadPage')}
                >
                  {siteTitle || 'SAP OCI Portal'}
                </h1>
                {/* Liquid Glass Role Badges */}
                <div className="flex items-center space-x-1 sm:space-x-2">
                  {user?.role === 'admin' && (
                    <div className={`relative inline-flex items-center px-2 py-0.5 ${isDarkMode
                      ? 'bg-red-500/20 border border-red-400/30 text-red-200'
                      : 'bg-red-100/60 border border-red-300/40 text-red-700'
                    } backdrop-blur-xl text-xs font-medium rounded-full shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]`}>
                      {t('header.administrator')}
                    </div>
                  )}
                  {user?.role === 'supervisor' && (
                    <div className={`relative inline-flex items-center px-2 py-0.5 ${isDarkMode
                      ? 'bg-purple-500/20 border border-purple-400/30 text-purple-200'
                      : 'bg-purple-100/60 border border-purple-300/40 text-purple-700'
                    } backdrop-blur-xl text-xs font-medium rounded-full shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]`}>
                      {t('header.supervisor')}
                    </div>
                  )}
                  {user?.role === 'purchaser' && (
                    <div className={`relative inline-flex items-center px-2 py-0.5 ${isDarkMode
                      ? 'bg-emerald-500/20 border border-emerald-400/30 text-emerald-200'
                      : 'bg-emerald-100/60 border border-emerald-300/40 text-emerald-700'
                    } backdrop-blur-xl text-xs font-medium rounded-full shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]`}>
                      {t('header.purchaser')}
                    </div>
                  )}
                  {user?.isGineexSupport && (
                    <div className={`relative inline-flex items-center px-2 py-0.5 ${isDarkMode
                      ? 'bg-amber-500/20 border border-amber-400/30 text-amber-200'
                      : 'bg-amber-100/60 border border-amber-300/40 text-amber-700'
                    } backdrop-blur-xl text-xs font-medium rounded-full shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]`}>
                      {t('header.gineexSupport')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Liquid Glass User Info & Actions */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            {/* Liquid Glass User Info - nur auf Desktop */}
            <div className="hidden lg:flex items-center mr-2 relative group">
              <div className={`absolute inset-0 ${isDarkMode 
                ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20' 
                : 'bg-gradient-to-br from-white/60 via-blue-50/40 to-white/60'
              } backdrop-blur-xl rounded-2xl border border-white/20 shadow-[0_4px_16px_rgba(0,0,0,0.2)]`}></div>
              <div className="relative flex items-center px-3 py-2">
                {/* Liquid Glass User Avatar */}
                <div className="relative group/avatar mr-2">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-full blur-sm"></div>
                  <div className={`relative w-8 h-8 ${isDarkMode 
                    ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60' 
                    : 'bg-gradient-to-br from-gray-300/60 via-slate-300/50 to-gray-400/60'
                  } backdrop-blur-xl rounded-full flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.2)] border border-white/20`}>
                    <User className={`w-4 h-4 ${isDarkMode ? 'text-white/90' : 'text-gray-600'}`} />
                    <div className="absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm"></div>
                  </div>
                </div>
                <div className="text-sm">
                  <div className={`font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-800'} drop-shadow-sm`}>
                    {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Liquid Glass Home Button */}
            <LiquidGlassIconButton
              onClick={() => onNavigate(ociMode ? 'catalogs' : 'home')}
              variant={(currentView === 'catalogs' || currentView === 'home') ? "orange" : "primary"}
              size="medium"
              icon={Home}
              title={ociMode ? t('header.backToCatalogs') : t('header.home')}
              isActive={currentView === 'catalogs' || currentView === 'home'}
            />

            {/* Liquid Glass Action Buttons */}
            <div className="flex items-center space-x-0.5 sm:space-x-1">
              {/* Liquid Glass BANF erstellen Button */}
              {inProcurementContext && (user?.role === 'purchaser' || user?.role === 'supervisor' || user?.role === 'admin') && (
                <LiquidGlassIconButton
                  onClick={() => onNavigate('create-banf')}
                  variant={currentView === 'create-banf' ? "orange" : "purple"}
                  size="medium"
                  icon={Plus}
                  title={t('header.newBanf')}
                  isActive={currentView === 'create-banf'}
                />
              )}

              {/* Liquid Glass BANF-Anfragen Button mit Entwürfe-Badge */}
              {inProcurementContext && (user?.role === 'supervisor' || user?.role === 'admin' || user?.role === 'purchaser') && (
                <LiquidGlassIconButton
                  onClick={() => onNavigate('purchase-requests')}
                  variant={currentView === 'purchase-requests' ? "orange" : "purple"}
                  size="medium"
                  icon={FileText}
                  badge={draftsCount > 0 ? draftsCount : (pendingRequestsCount > 0 ? pendingRequestsCount : null)}
                  badgeColor={draftsCount > 0 ? "purple" : "orange"}
                  title={
                    draftsCount > 0
                      ? t('header.banfRequestsDrafts', { drafts: draftsCount, pending: pendingRequestsCount })
                      : user?.role === 'purchaser'
                      ? t('header.myBanfRequests', { count: pendingRequestsCount })
                      : user?.role === 'supervisor'
                      ? t('header.banfRequestsCostCenter', { costCenter: user.costCenter, count: pendingRequestsCount })
                      : t('header.banfRequestsAll', { count: pendingRequestsCount })
                  }
                  isActive={currentView === 'purchase-requests'}
                />
              )}

              {/* Liquid Glass Admin Button */}
              {user?.role === 'admin' && (
                <LiquidGlassIconButton
                  onClick={() => onNavigate('admin')}
                  variant={currentView === 'admin' ? "orange" : "danger"}
                  size="medium"
                  icon={Shield}
                  title={t('header.adminArea')}
                  isActive={currentView === 'admin'}
                />
              )}
              
              {/* Benachrichtigungs-Glocke — modulunabhängig sichtbar (Taras 29.07.), aber:
                  nicht im OCI-Punch-out (Einweg-Flow, kein Absprung anbieten) und nur für
                  Rollen, für die überhaupt Benachrichtigungsdaten geladen werden. */}
              {user && !ociMode && ['purchaser', 'supervisor', 'admin'].includes(user?.role) && (
                <NotificationBell
                  user={user}
                  purchaseRequests={purchaseRequests}
                  drafts={drafts}
                  onOpenRequests={(tab) => {
                    if (typeof onOpenRequests === 'function') onOpenRequests(tab);
                    else onNavigate('purchase-requests');
                  }}
                />
              )}

              {/* Liquid Glass Settings Button */}
              <LiquidGlassIconButton
                onClick={() => onNavigate('settings')}
                variant={currentView === 'settings' ? "orange" : "primary"}
                size="medium"
                icon={Settings}
                title={t('header.settings')}
                isActive={currentView === 'settings'}
              />

              {/* Liquid Glass Cart Button — nur im Beschaffungskontext */}
              {inProcurementContext && (
              <LiquidGlassIconButton
                onClick={() => onNavigate('cart')}
                variant={currentView === 'cart' ? "orange" : "primary"}
                size="medium"
                icon={ShoppingCart}
                badge={cartItemCount > 0 ? cartItemCount : null}
                badgeColor="red"
                title={t('header.cart', { count: cartItemCount })}
                isActive={currentView === 'cart'}
              />
              )}
            </div>
            
            {/* Liquid Glass Logout Button */}
            <div className={`border-l ${isDarkMode ? 'border-white/20' : 'border-gray-300/40'} ml-2 sm:ml-3 pl-2 sm:pl-3`}>
              <button 
                onClick={onLogout}
                className="relative group/logout flex items-center text-sm transition-all duration-200 px-2 py-1.5 sm:px-3 sm:py-2 rounded-2xl transform hover:scale-105"
                title={ociMode ? t('header.backToSap') : t('header.logout')}
              >
                {/* Liquid Glass Background */}
                <div className={`absolute inset-0 ${isDarkMode 
                  ? 'bg-gray-900/20 border border-white/20' 
                  : 'bg-white/30 border border-blue-200/30'
                } backdrop-blur-xl rounded-2xl opacity-0 group-hover/logout:opacity-100 transition-opacity duration-200`}></div>
                <div className="absolute inset-0 bg-red-500/10 rounded-2xl opacity-0 group-hover/logout:opacity-100 transition-opacity duration-200"></div>
                
                {/* Content */}
                <ArrowLeft className={`relative w-4 h-4 mr-1 sm:mr-2 ${isDarkMode ? 'text-white/70' : 'text-gray-600'} group-hover/logout:text-red-300 transition-colors duration-200`} />
                <span className={`relative hidden sm:inline ${isDarkMode ? 'text-white/70' : 'text-gray-600'} group-hover/logout:text-red-300 transition-colors duration-200 drop-shadow-sm`}>
                  {ociMode ? t('header.backToSap') : t('header.logout')}
                </span>
              </button>
            </div>
            
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className={`relative ml-2 sm:ml-3 group/lang overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-105 active:scale-95`}
              title={language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
            >
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-br from-blue-600/30 via-indigo-600/25 to-blue-700/30 border border-blue-400/30'
                : 'bg-gradient-to-br from-blue-100/60 via-indigo-100/50 to-blue-200/60 border border-blue-300/40'
              } backdrop-blur-xl rounded-2xl`}></div>
              <div className={`relative px-2.5 py-1.5 sm:px-3 sm:py-2 font-bold text-xs sm:text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-700'} drop-shadow-sm`}>
                {language === 'de' ? 'DE' : 'EN'}
              </div>
            </button>

            {/* Liquid Glass Theme Toggle Button */}
            <LiquidGlassIconButton
              onClick={toggleTheme}
              variant={isDarkMode ? "warning" : "secondary"}
              size="medium"
              icon={isDarkMode ? Sun : Moon}
              title={isDarkMode ? t('header.lightMode') : t('header.darkMode')}
              className="ml-1 sm:ml-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;